import React, { useEffect, useState } from "react";
import classes from "./styles.module.scss";
import Tabs from "../../../../components/tabs/tabs.component";
import { useTranslation } from "react-i18next";
import { useKato } from "../../../../contexts/kato.context";
import { KatoType } from "../../../../types/kato.types";
import {
  MenuCloseIcon,
  SortIcon,
  TrendDown,
  TrendUp,
} from "../../../../assets/icons";
import { SERIcons } from "../../../../consts/other.conts";
import { useShared } from "../../../../contexts/shared.context";
import FullscreenClose from "./fullscreen-close.component";
import { getIncreaseVal } from "../../../../helpers/utils";

const order = [
  "INVESTMENT",
  "INDUSTRIAL_PRODUCTION",
  "MINING_INDUSTRY",
  "MANUFACTURING_INDUSTRY",
  "CONSTRUCTION",
  "AGRICULTURE",
  "TRANSPORT_WAREHOUSING",
  "HOUSE_COMMISSIONING",
];

const SerFullscreen = () => {
  const { t } = useTranslation();
  const { currentLevel } = useKato();
  const { basicStats } = useKato().katoGeneralInfo;
  const tabs = [
    {
      key: "SER",
      label: t("cards.ser-card.SER"),
    },
    {
      key: "BUDGET",
      label: t("cards.ser-card.BUDGET"),
    },
    {
      key: "MCB",
      label: t("cards.ser-card.MCB"),
    },
    {
      key: "EMPLOYMENT",
      label: t("cards.ser-card.EMPLOYMENT"),
    },
    {
      key: "LIVESTOCK",
      label: t("cards.ser-card.LIVESTOCK"),
    },
  ];
  const [activeTab, setActiveTab] = useState<string>(tabs[0].key);
  const [data, setData] = useState<any>({
    SER: [],
    BUDGET: [],
    MCB: [],
    EMPLOYMENT: [],
    LIVESTOCK: [],
  });

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const config = {
    SER: [
      [
        {
          key: "name",
          label: t("cards.ser-card.table.name"),
          visible: true,
          colSpan: 2,
          rowSpan: 2,
        },
        {
          key: "ifo",
          label: t("cards.ser-card.table.ifo"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "increase",
          label: t("cards.ser-card.table.increase"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "value",
          label: t("cards.ser-card.table.value"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "regionPlace",
          label: t("cards.ser-card.table.regionPlace", {
            type:
              currentLevel === KatoType.OBLAST
                ? t("cards.ser-card.regions")
                : t("cards.ser-card.districts"),
          }),
          visible: true,
          colSpan: 2,
          rowSpan: 1,
        },
      ],
      [
        {
          key: "ifoPlace",
          label: t("cards.ser-card.table.ifo"),
          visible: true,
        },
        {
          key: "place",
          label: t("cards.ser-card.table.place", {
            type:
              currentLevel === KatoType.OBLAST ? t("billion") : t("million"),
          }),
          visible: true,
        },
      ],
    ],
    BUDGET: [
      [
        {
          key: "name",
          label: t("cards.ser-card.table.name"),
          visible: true,
          colSpan: 2,
          rowSpan: 2,
        },
        {
          key: "value",
          label: t("cards.ser-card.table.value"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "development",
          label: t("cards.ser-card.table.development"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "increase",
          label: t("cards.ser-card.table.increase"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "regionPlace",
          label: t("cards.ser-card.table.regionPlace", {
            type: t("cards.ser-card.table.districts"),
          }),
          visible:
            currentLevel === KatoType.REGION || currentLevel === KatoType.CITY,
          colSpan: 2,
          rowSpan: 1,
        },
      ],
      [
        {
          key: "ifoPlace",
          label: t("cards.ser-card.table.ifoPlace"),
          visible:
            currentLevel === KatoType.REGION || currentLevel === KatoType.CITY,
        },
        {
          key: t("cards.ser-card.table.place", {
            type:
              currentLevel === KatoType.OBLAST ? t("billion") : t("million"),
          }),
          label:
            currentLevel === KatoType.OBLAST
              ? "Объем, млр., тенге"
              : "Объем, млн., тенге",
          visible:
            currentLevel === KatoType.REGION || currentLevel === KatoType.CITY,
        },
      ],
    ],
    OTHER: [
      [
        {
          key: "name",
          label: t("cards.ser-card.table.name"),
          visible: true,
          colSpan: 2,
          rowSpan: 2,
        },
        {
          key: "ifo",
          label: t("cards.ser-card.table.ifo"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "increase",
          label: t("cards.ser-card.table.increase"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "value",
          label: t("cards.ser-card.table.value"),
          visible: true,
          rowSpan: 2,
        },
        {
          key: "regionPlace",
          label: t("cards.ser-card.table.regionPlace", {
            type: t("cards.ser-card.table.districts"),
          }),
          visible:
            currentLevel === KatoType.REGION || currentLevel === KatoType.CITY,
          colSpan: 2,
          rowSpan: 1,
        },
      ],
      [
        {
          key: "ifoPlace",
          label: t("cards.ser-card.table.ifoPlace"),
          visible:
            currentLevel === KatoType.REGION || currentLevel === KatoType.CITY,
        },
        {
          key: "place",
          label: t("cards.ser-card.table.place", {
            type:
              currentLevel === KatoType.OBLAST ? t("billion") : t("million"),
          }),
          visible:
            currentLevel === KatoType.REGION || currentLevel === KatoType.CITY,
        },
      ],
    ],
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }

    setData((prevData: any) => ({
      ...prevData,
      [activeTab]: prevData[activeTab]?.slice().sort((a: any, b: any) => {
        const aValue = a[key as keyof any] ?? "";
        const bValue = b[key as keyof any] ?? "";

        if (
          key === "name" &&
          typeof aValue === "string" &&
          typeof bValue === "string"
        ) {
          if (sortDirection === "asc") {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        } else {
          const aNum = +aValue.toString().replaceAll(",", ".") || 0;
          const bNum = +bValue.toString().replaceAll(",", ".") || 0;

          if (sortDirection === "asc") {
            return aNum - bNum;
          } else {
            return bNum - aNum;
          }
        }
      }),
    }));
  };

  const generateTableHeaders = () => {
    return (
      <thead>
        <tr>
          {(["SER", "BUDGET"].includes(activeTab)
            ? config[activeTab as keyof typeof config]
            : config["OTHER"])[0].map(
            (item: any, index: number) =>
              item.visible && (
                <th
                  key={index}
                  onClick={() => handleSort(item.key)}
                  colSpan={item.colSpan || 1}
                  rowSpan={item.rowSpan || 1}
                  className={item.key === "regionPlace" ? "no-border" : ""}
                >
                  <div className={classes["ser-fullscreen__table-header"]}>
                    <div className={classes["ser-fullscreen__label"]}>
                      {item.label}
                    </div>
                    {item.key !== "regionPlace" && <SortIcon />}
                  </div>
                </th>
              )
          )}
        </tr>
        <tr className="no-border">
          {(["SER", "BUDGET"].includes(activeTab)
            ? config[activeTab as keyof typeof config]
            : config["OTHER"])[1].map(
            (item: any, index: number) =>
              item.visible && (
                <th key={index}>
                  <div className={classes["ser-fullscreen.label"]}>
                    {item.label}
                  </div>
                </th>
              )
          )}
        </tr>
      </thead>
    );
  };

  const renderTableBody = (items: any[], showPlaceFields = false) => (
    <tbody>
      {items.map((item: any, index: number) => {
        const isBudget = activeTab === "BUDGET";
        const isIncreasePositive = item.increase > 0;
        const isDevelopmentPositive = +item.development > 0;
        const maxPlace = currentLevel !== KatoType.OBLAST ? "/ 13" : "/ 20";

        const renderChangeCell = (value: number, positive: boolean) => (
          <div
            className={classes["ser-fullscreen__table-change"]}
            style={{
              color: positive ? "var(--color-success)" : "var(--color-failure)",
            }}
          >
            {positive ? <TrendUp /> : <TrendDown />} {value} {item.add || "%"}
          </div>
        );

        return (
          <tr key={index}>
            <td>
              <div className={classes["ser-fullscreen__table-icon"]}>
                {item.icon}
              </div>
            </td>
            <td>{item.name}</td>
            <td>{item.ifo ?? "-"}</td>
            <td>{renderChangeCell(item.increase, isIncreasePositive)}</td>
            <td>
              {isBudget
                ? renderChangeCell(+item.development, isDevelopmentPositive)
                : item.value !== "0"
                ? `${item.value} ${item.add}`
                : `- ${item.add}`}
            </td>
            {showPlaceFields && (
              <>
                <td>
                  {item.ifoPlace ?? "-"} {maxPlace}
                </td>
                <td>
                  {item.place ?? "-"} {maxPlace}
                </td>
              </>
            )}
          </tr>
        );
      })}
    </tbody>
  );

  const getTableBody = () => {
    const showPlaceFields =
      currentLevel === KatoType.REGION || currentLevel === KatoType.CITY;

    switch (activeTab) {
      case "SER":
        return renderTableBody(data.SER, true);
      case "BUDGET":
      case "MCB":
      case "EMPLOYMENT":
      case "LIVESTOCK":
        return renderTableBody(data[activeTab], showPlaceFields);
      default:
        return <tbody />;
    }
  };

  useEffect(() => {
    if (!basicStats) return;

    const TYPES = tabs.map((tab) => tab.key);

    const DATA = Object.fromEntries(
      TYPES.map((type) => [
        type,
        basicStats.filter((item) => item.type === type),
      ])
    );

    const formatItem = (
      item: any,
      subTypeKey: string,
      namePrefix: string,
      addRule?: (subType: string) => string,
      extraFields?: Record<string, any>
    ) => ({
      ...item,
      value: item.currentYearVal?.toLocaleString() ?? "-",
      increase: getIncreaseVal(item, basicStats) ?? "-",
      place: item.additionalInfo?.place?.toString() ?? "-",
      icon: SERIcons[subTypeKey] ?? null,
      name: t(`${namePrefix}.${subTypeKey}`),
      add: addRule?.(subTypeKey) ?? "",
      ifoPlace: item.ifoPlace ?? "-",
      ifo: item.ifo?.toString() ?? "-",
      ...extraFields,
    });

    const SER = order
      .map((key: string) => {
        const item = DATA.SER?.find((el: any) => el.subType === key);
        return item
          ? formatItem(item, key, "cards.ser-card", (subType) =>
              subType === "HOUSE_COMMISSIONING" ? "кв. м." : ""
            )
          : undefined;
      })
      .filter(Boolean);

    const BUDGET = DATA.BUDGET?.map((item: any) =>
      formatItem(item, item.subType, "cards.ser-card", undefined, {
        development: item.additionalInfo?.development ?? "-",
      })
    );

    const MCB = DATA.MCB?.map((item: any) =>
      formatItem(item, item.subType, "cards.ser-card", (subType) =>
        subType === "CURRENT_MSP" ? "ед." : ""
      )
    );

    const EMPLOYMENT = DATA.EMPLOYMENT?.map((item: any) =>
      formatItem(
        item,
        item.subType,
        "cards.ser-card",
        (subType) => {
          if (subType === "AVERAGE_MONTHLY_SALARY") return "тенге";
          if (subType === "NEW_WORKPLACE") return "ед.";
          return "чел.";
        },
        {
          increaseAdd: item.subType === "UNEMPLOYED" ? "чел." : null,
        }
      )
    );

    const LIVESTOCK = DATA.LIVESTOCK?.map((item: any) =>
      formatItem(item, item.subType, "cards.ser-card", (subType) => {
        if (["MEAT", "COW_MILK"].includes(subType)) return "тонн";
        if (subType === "CHICKEN_EGG") return "тыс. шт.";
        return "";
      })
    );

    setData({ SER, BUDGET, MCB, EMPLOYMENT, LIVESTOCK });
  }, [basicStats]);

  if (!basicStats) return null;
  return (
    <div className={classes["ser-fullscreen"]}>
      <FullscreenClose />
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      {data && (
        <div className={classes["ser-fullscreen__table"]}>
          <div className={classes["ser-fullscreen__table-wrapper"]}>
            <table>
              {generateTableHeaders()}
              {getTableBody()}
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SerFullscreen;
